import {
  ColDef,
  GetContextMenuItemsParams,
  GetDetailRowDataParams,
  GetRowIdParams,
  GridReadyEvent,
  ICellRendererParams,
  IDetailCellRendererParams,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-balham.css"; // Optional Theme applied to the grid
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"; // import { AgGridReact } from "ag-grid-react"; // AG Grid Component

import {
  defaultColDef,
  defaultColDefActionSummary,
  expandRowsFromSelectPanelAciton,
  GenerateActionSummaryCustomStyles,
  getColumnWidthMultiplier,
  getFontSizePx,
  getHeaderHeight,
  getRowHeight,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";

// Set trade data from the store
import expandedRowsStore from "@/store/expandedRowsStore";
import tradeDataStore from "@/store/tradeDataStore";
import featureFlagStore from "@/store/featureFlagStore";

import { returnLegColDefsOP } from "@/components/widgets/tradeActionSummary/helper/colDefs/legColDefsOP";
import { returnLegColDefsRP } from "@/components/widgets/tradeActionSummary/helper/colDefs/legColDefsRP";
import {
  IColDefsObject,
  returnColDefsForMainActionSummaryTable,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryMainTableColDefs";
import DynamicCellEditorQuoteDollarFraction from "@/components/widgets/tradeActionSummary/helper/cellEditors/DynamicCellEditorQuoteDollarFraction";
import {
  applyColumnStateToAGGridTable,
  applyFiltersToAGGridTable,
  manageGridEventListeners,
  readGridRefAndUpdateStoreAndDatabaseWithNewValues,
} from "@/lib/AGGridHelpers";
import AGGridNoRowsOverlay from "@/components/UI/AGGridNoRowsOverlay";
import {
  AG_GRID_LOADING_TEMPLATE,
  syncNoRowsOverlay,
} from "@/lib/agGridOverlayHelper";
import {
  processDataFromClipboardPreventMulti,
  suppressMultiCellDelete,
} from "@/lib/clipboardValidation/preventMultiCellOperations";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { TradeType } from "@/lib/interfaces and Enums/ENUMTradeType";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import {
  isWidgetInCurrentWorkspace,
  returnOpenWidgetsObject,
} from "@/lib/widgetHelperFunctions";
import useTableUIStore from "@/store/tableUIStore";
import tradeBlockDataStore from "@/store/tradeBlockDataStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import requestInformationStore from "@/store/requestInformationStore";
import { anyOpTradeHasPendingRequest } from "@/store/requestInformationStoreACTIONS";
import { TRADE_SETTINGS_TOOL_PANEL_ID } from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/toggleTradeSettingsPanel";
import { useTheme } from "next-themes";
import { IqueryForExecutionSummaryHistorical } from "../../../services/ActionSummaryTableSERVICES";
import GetExecutionContextMenuItems from "./ActionSummaryOP/selectPanel/GetExecutionContextMenuItems";
import SelectPanelIndex from "./ActionSummaryOP/selectPanel/SelectPanelIndex";
import {
  actionSummaryFilterChangeHandler,
  findChangedDataForMainActionSummary,
  handleRedrawingRowsForActionSummaryTable,
  handleShowOrHideColsFromNodeExpand,
  IActionSummaryTable,
  IdefaultExpandedRowContext,
  IFindChangedDataOutput,
  ITradeDetails,
  masterDetailAtLegLevelForOrders,
  masterDetialAtERLevel,
  redrawRowsViaESID,
  returnColDefsForDetailTableAtActionSummary,
  returnRowDataAtDetailTable,
  returnRowDataAtNestedLegTable,
  returnRowIDAtFirstDetailTable,
  setLegTableToExpanded,
  subscribeToRequestInfoFlash,
} from "./ActionSummaryTableHelperFunctions";
import GetContextMenuItemsActionSummary from "./contextMenusRightClick/GetContextMenuItemsActionSummary";
import { recordExpiryAnchorFromEvent } from "./contextMenusRightClick/expiryContextMenuItem";
import ExpiryEditPopoverHost from "./helper/cellRenderers/MainTableActionSummaryESLevel/ExpiresCellRenderer/ExpiryEditPopoverHost";
import {
  actionSummarySidebar,
  getActionSummarySidebarIcons,
} from "./helper/actionSummarySidebar";
import { useCellSelectionManager } from "./helper/useCellSelectionManager";
import HistoricalSearchSidebarContent from "./historical/HistoricalSearchSidebarContent";
import { overlayLiveTradesOnHistorical } from "./historical/historicalRowsHelper";
import TradeSettingsSidebarContent from "./tradeSettings/TradeSettingsSidebarContent";
import agGridFlashNotifications from "@/store/notifications/agGridFlashNotifications";
import historicalSearchStore, {
  returnHistoricalSearch,
} from "@/store/historicalSearchStore";
import { handleTradeNotification } from "@/lib/notificationHelper";

// Shown while rowData is undefined (summary fetch still in flight) instead of AG Grid's
// default "No Rows To Show". Deliberately not ag-overlay-loading-center - that class is
// what draws the grey card; styling lives in globals.css.
// LQC-637/LQC-1744/LQC-1844 — owns the sidebar-tab pulse (the `requestPendingSideBar`
// class) in ISOLATION from the grid. It subscribes to requestInfo and re-renders on
// every request change, but `children` (the grid wrapper + <AgGridReact>) is created
// once by ActionSummaryTable and passed through by reference, so React bails out of
// re-rendering the grid subtree when only the pulse flips. This is what stops a
// settings Update (which refreshes requestInfo) from collapsing the expanded trade:
// ActionSummaryTable no longer subscribes to requestInfo, so it doesn't re-render and
// AG Grid never drops its open master/detail row. The pulse class sits on this outer
// container (an ancestor of the grid), so the globals.css rules use descendant
// selectors (`.requestPendingSideBar .ag-theme-balham …`).
const RequestPendingWrapper = ({
  rowData,
  children,
}: {
  rowData: { esid?: string; role?: string }[];
  children: React.ReactNode;
}) => {
  const requestInfo = requestInformationStore((state) => state.requestInfo);
  const anyOpPendingRequest = anyOpTradeHasPendingRequest(requestInfo, rowData);
  return (
    <div
      className={`flex h-full flex-col${anyOpPendingRequest ? " requestPendingSideBar" : ""}`}
    >
      {children}
    </div>
  );
};

const ActionSummaryTable = ({
  cryptoID,
  getAccessTokenSilently,
  user,
  dataForMainTable,
  isMiniTable = false,
  tradeData,
}: IActionSummaryTable) => {
  // LQC: the summary fetch finished (either way). Until it has, an empty grid is
  // "loading", not "no rows".
  const tradeDataLoadAttempted = tradeDataStore(
    (state) => state.tradeDataLoadAttempted,
  );

  let dataToLoadInTable: ExecutionStrategy[] | undefined = !isMiniTable // dynamically choose which data is loaded.
    ? dataForMainTable // used in main table
    : tradeData?.length == 0
      ? []
      : [tradeData?.find((trade) => trade.esid == dataForMainTable.esid)]; // Used for mini tile

  // undefined rowData is AG Grid's own "loading" signal - it shows the loading overlay
  // instead of "No Rows To Show" until real data (or an empty result) lands.
  if (!tradeDataLoadAttempted && dataToLoadInTable?.length == 0) {
    dataToLoadInTable = undefined;
  }

  // May be able to remove the tradeBlocksData and not watch it for rows to flash if we add a value to the trade data
  // Example - we might add something in the Details section of the string to show NPV.
  const tradeBlockData = tradeBlockDataStore((state) => state.tradeBlockData);

  // Subscribe to esidsToExpand for validation-triggered row expansion
  const esidsToExpand = tradeDataStore((state) => state.esidsToExpand);

  // LQC-957: gate filter restoration on the Action Summary API returning so set-filter values
  // aren't dropped against an empty grid. Workspaces is already guaranteed loaded — this widget
  // can't mount until ReactGridLayouts has hydrated openWidgets from workspacesTabsStore.
  const tradeDataHasReturned = tradeDataStore(
    (state) => state.tradeDataHasReturned,
  );

  const gridRef = useRef<AgGridReact>(null);

  // Stores each column's ORIGINAL minWidth (keyed by colId) so font-size
  // scaling always scales from the base value instead of compounding off the
  // previously-scaled minWidth. Reset whenever columnDefs are rebuilt.
  const baseMinWidthsRef = useRef<Record<string, number>>({});

  const previousFilterModel = useRef<any>({});

  // Drives the orange filter indicator on the sidebar "Filters" tab button.
  // True whenever any column filter is active on the top-level grid.
  const [isAnyFilterActive, setIsAnyFilterActive] = useState(false);

  const [rowData, setRowData] = useState(dataToLoadInTable);

  // LQC-637/LQC-1744 — the Trade Settings sidebar-tab pulse (driven by an
  // outstanding RP request) is owned by <RequestPendingWrapper> below, NOT here.
  // LQC-1844: ActionSummaryTable deliberately does NOT subscribe to requestInfo —
  // re-rendering this component on a request change collapses any expanded master
  // row (AG Grid drops the open detail). The wrapper isolates that reactivity.

  const { theme } = useTheme();
  const tableStripeOddTrue = useTableUIStore(
    (state) => state.tableStripeTradeBlotterOn,
  ); // used to change the table stripe with settings.
  const fontSize = useTableUIStore((state) => state.fontSize);
  const rowHeight = useTableUIStore((state) => state.rowHeight);
  const tradeBlotterColorLight = useTableUIStore(
    (state) => state.tradeBlotterColorLight,
  );
  const tradeBlotterColorDark = useTableUIStore(
    (state) => state.tradeBlotterColorDark,
  );
  const tradeBlotterBidAskEnabled = useTableUIStore(
    (state) => state.tradeBlotterBidAskEnabled,
  );
  const tradeBlotterBidColorLight = useTableUIStore(
    (state) => state.tradeBlotterBidColorLight,
  );
  const tradeBlotterBidColorDark = useTableUIStore(
    (state) => state.tradeBlotterBidColorDark,
  );
  const tradeBlotterAskColorLight = useTableUIStore(
    (state) => state.tradeBlotterAskColorLight,
  );
  const tradeBlotterAskColorDark = useTableUIStore(
    (state) => state.tradeBlotterAskColorDark,
  );
  const tradeBlotterFlashColorLight = useTableUIStore(
    (state) => state.tradeBlotterFlashColorLight,
  );
  const tradeBlotterFlashColorDark = useTableUIStore(
    (state) => state.tradeBlotterFlashColorDark,
  );
  const testFlashTradeBlotter = useTableUIStore(
    (state) => state.testFlashTradeBlotter,
  );

  // Use Zustand store for expandedRowsContext - scoped by cryptoID
  // This ensures each widget instance maintains its own independent state
  const expandedRowsContext = expandedRowsStore((state) =>
    cryptoID ? state.getExpandedRows(cryptoID) : [],
  );

  // Zustand store for setting the duration of the row flashing time.
  const storeRowFlashDuration = agGridFlashNotifications(
    (state) => state.tradeBlotterFlashDuration,
  );

  // Create stable setter functions using useMemo (cryptoID doesn't change after mount)
  const setExpandedRowsContext = useMemo(() => {
    return (
      rowsOrUpdater:
        | IdefaultExpandedRowContext[]
        | ((
            prev: IdefaultExpandedRowContext[],
          ) => IdefaultExpandedRowContext[]),
    ) => {
      if (cryptoID) {
        const currentRows = expandedRowsStore
          .getState()
          .getExpandedRows(cryptoID);
        const newRows =
          typeof rowsOrUpdater === "function"
            ? rowsOrUpdater(currentRows)
            : rowsOrUpdater;
        expandedRowsStore.getState().setExpandedRows(cryptoID, newRows);
      }
    };
  }, [cryptoID]);

  // Keep ref for backward compatibility with code that needs synchronous access
  const currentExpandedRowsContextRef = useRef<IdefaultExpandedRowContext[]>(
    [],
  );

  // Use Zustand store for most recently updated esid - scoped by cryptoID
  const expandedRowsContextMostRecentlyUpdated = expandedRowsStore((state) =>
    cryptoID ? state.getMostRecentlyUpdated(cryptoID) : "",
  );

  // Create stable setter function using useMemo (cryptoID doesn't change after mount)
  const setExpandedRowsContextMostRecentlyUpdated = useMemo(() => {
    return (esidOrUpdater: string | ((prev: string) => string)) => {
      if (cryptoID) {
        const currentEsid = expandedRowsStore
          .getState()
          .getMostRecentlyUpdated(cryptoID);
        const newEsid =
          typeof esidOrUpdater === "function"
            ? esidOrUpdater(currentEsid)
            : esidOrUpdater;
        expandedRowsStore.getState().setMostRecentlyUpdated(cryptoID, newEsid);
      }
    };
  }, [cryptoID]);

  const [allASChangedData, setAllASChangedData] = useState<any>([]);

  // Variable to track if the user wants the widget to be in an action summary mode - If so this will contain the data for the table and the dates that the user was looking at.
  // LQC-1885 - held in a store so the widget header and right-click menu can read it.
  const historicalSearchKey = cryptoID ?? "";
  const historicTradeDataForMainTable = historicalSearchStore((state) =>
    returnHistoricalSearch(state.searchByCryptoId, historicalSearchKey),
  );
  const setHistoricTradeDataForMainTable = useCallback(
    (
      update:
        | IqueryForExecutionSummaryHistorical
        | ((
            prev: IqueryForExecutionSummaryHistorical,
          ) => IqueryForExecutionSummaryHistorical),
    ) =>
      historicalSearchStore
        .getState()
        .setHistoricalSearch(historicalSearchKey, update),
    [historicalSearchKey],
  );

  // Matches the old local-state lifetime: leaving the workspace ends the search.
  useEffect(() => {
    return () => {
      historicalSearchStore
        .getState()
        .clearHistoricalSearch(historicalSearchKey);
    };
  }, [historicalSearchKey]);

  const esidsToFlash = tradeDataStore((state) => state.eisdsToFlash);

  useEffect(() => {
    if (historicTradeDataForMainTable.viewingHistoricalTable) {
      setRowData(
        overlayLiveTradesOnHistorical(
          historicTradeDataForMainTable.historicTradeDataForDataRange,
          dataToLoadInTable,
        ),
      );
    } else {
      setRowData(dataToLoadInTable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicTradeDataForMainTable]);

  useEffect(() => {
    //APPLYING THE CHANGES TO THE Table
    const oldRowData = rowData;
    const newRowData = dataToLoadInTable;

    const changedData = findChangedDataForMainActionSummary(
      oldRowData ?? [],
      newRowData ?? [],
      "esid",
    );
    setAllASChangedData(changedData ? changedData : []);

    if (historicTradeDataForMainTable.viewingHistoricalTable) {
      setRowData(
        overlayLiveTradesOnHistorical(
          historicTradeDataForMainTable.historicTradeDataForDataRange,
          dataToLoadInTable,
        ),
      );
    } else {
      setRowData(dataToLoadInTable);
    }

    const arrToFlash = tradeDataStore.getState().eisdsToFlash;
    const nodeArrToFlash: any[] = [];
    const notificationsArray: any[] = [];
    if (gridRef.current && gridRef.current.api && arrToFlash.length > 0) {
      arrToFlash.forEach((element) => {
        const node = gridRef.current?.api.getRowNode(element);
        if (node != undefined) {
          nodeArrToFlash.push(node);
          const foundTrade = dataToLoadInTable?.find(
            (trade) => trade.esid == element,
          );
          if (foundTrade) {
            notificationsArray.push(foundTrade);
          }
        }
      });
      gridRef.current?.api.flashCells({
        rowNodes: nodeArrToFlash,
        fadeDuration: 1000,
        flashDuration: storeRowFlashDuration,
      });
      // Browser notifications are now handled by handleTradeNotification()
      // in ActionSummaryTableHelperFunctions.tsx, which respects the
      // notification preferences store toggles.

      setTimeout(() => {
        tradeDataStore.getState().updateEisdsToFlash([]);
      }, 2);
    }

    // Notify for newly added trades (e.g. RP receiving a trade with status "Received")
    // This must be outside the eisdsToFlash gate — new trades may arrive without an esid to flash.
    if (changedData && changedData?.length == 1 && changedData[0]) {
      if ("newItem" in changedData[0]) {
        if (changedData[0]?.type == "added") {
          const addedTrade = changedData[0].newItem as any;
          handleTradeNotification({
            newStatus: addedTrade.status,
            tradeId: addedTrade.esid,
            role: addedTrade.role,
            owner: addedTrade.owner,
            latestActivity: addedTrade.latestActivity,
          });
        }
      }
    }

    if (!isMiniTable) {
      // Function to expand any nodes that were created via the trade creation widget's 'select panel' action rather than the 'save trade' action.
      expandRowsFromSelectPanelAciton(gridRef);
      // applyFiltersToAGGridTable(gridRef, openWidgetObject); // eslint-disable-next-line react-hooks/exhaustive-deps
      // Added ESID props to the dependecy ARR - If the Table is built again, it will update the data.
    }
    // tradeDataLoadAttempted: a failed summary fetch leaves tradeData untouched, so
    // without it rowData would stay undefined and the loading overlay never clears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataForMainTable,
    tradeData,
    tradeBlockData,
    esidsToFlash,
    tradeDataLoadAttempted,
  ]);

  // React to esidsToExpand changes (e.g., from validation errors in updateFillsStoreAction)
  useEffect(() => {
    if (!isMiniTable && esidsToExpand.length > 0) {
      expandRowsFromSelectPanelAciton(gridRef);
    }
  }, [esidsToExpand, isMiniTable]);

  // Test flash from settings — flash the top row when triggered
  useEffect(() => {
    if (testFlashTradeBlotter > 0 && gridRef.current?.api) {
      const firstRow = gridRef.current.api.getDisplayedRowAtIndex(0);
      if (firstRow) {
        gridRef.current.api.flashCells({
          rowNodes: [firstRow],
          flashDuration: storeRowFlashDuration,
          fadeDuration: 1000,
        });
      }
    }
  }, [testFlashTradeBlotter]);

  // LQC-1844 — flash the affected ES row(s) when request-info status changes,
  // imperatively (no re-render) so the open master/detail row isn't collapsed.
  // Logic lives in subscribeToRequestInfoFlash; see its comment for the rationale.
  useEffect(() => subscribeToRequestInfoFlash(gridRef), []);

  // Persist column + filter state. Reads live grid state, so only runs while the grid is alive.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const saveColumnStateNow = useCallback(() => {
    if (cryptoID == undefined || !safeToSaveRef.current) return;
    if (!gridRef.current?.api) return;
    // Before filters are restored the live filter is {} — keep the stored one so a save can't wipe it.
    const filterModelOverride = filtersAppliedRef.current
      ? undefined
      : returnOpenWidgetsObject(cryptoID)?.filterModel;
    readGridRefAndUpdateStoreAndDatabaseWithNewValues(
      gridRef,
      getAccessTokenSilently,
      cryptoID,
      filterModelOverride,
    );
  }, [cryptoID, getAccessTokenSilently]);

  // Stable debounce (single timer ref rather than a per-render debounce instance).
  const debouncedSaveColumnState = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(saveColumnStateNow, 600);
  }, [saveColumnStateNow]);

  // Cancel any pending save on unmount. Unmount happens on tab switch, when the active-tab
  // pointer has already moved — flushing here would write this blotter's state into the wrong tab.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const expandedRowsContextCurrent = currentExpandedRowsContextRef.current;

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 130,
    };
  }, []);

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.esid;
  }, []);

  // Add a ref to track if initial settings have been applied
  const initialSettingsAppliedRef = useRef(false);
  const firstDataApplied = useRef(false);
  // filtersAppliedRef = filter restore done (live filters authoritative). safeToSaveRef
  // blocks saves until the saved layout is applied, so a load-time save can't overwrite it
  // with defaults; armed in onGridReady after apply (not gated on row data). Both reset
  // per grid (re)creation.
  const filtersAppliedRef = useRef(false);
  const safeToSaveRef = useRef(false);
  // Attpmt to have the animation of the grid set to false to start - then flipped once the table has loaded.
  const [hasBuiltGrid, setHasBuiltGrid] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setHasBuiltGrid(true);
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstDataApplied.current]);

  const onGridReady = useCallback(
    async (_params: GridReadyEvent) => {
      // New grid instance (mount / tab switch / refresh): re-arm restore and block saves until
      // it completes. onRowDataUpdated runs restoreSavedFilters once rows land on this grid.
      filtersAppliedRef.current = false;
      safeToSaveRef.current = false;

      // Column state (widths/order/visibility/sort) doesn't depend on API data — apply it
      // immediately so the first paint reflects the user's saved layout.
      // Filter restoration is gated separately in the useEffect below (see LQC-957).
      setTimeout(() => {
        const latestOpenWidgetObject = cryptoID
          ? returnOpenWidgetsObject(cryptoID)
          : undefined;

        if (
          !initialSettingsAppliedRef.current &&
          gridRef.current &&
          latestOpenWidgetObject?.columnStateForWidget
        ) {
          applyColumnStateToAGGridTable(gridRef, latestOpenWidgetObject);
          initialSettingsAppliedRef.current = true;
        }

        // Layout applied, safe to persist edits (not gated on row data, so load-window
        // changes aren't dropped). LQC-1740: trade tiles persist column state too, so
        // the gate is now "backed by a real workspace widget" rather than !isMiniTable.
        // The pop-out's synthetic cryptoID has no widget entry, so it stays non-persisting.
        if (isWidgetInCurrentWorkspace(cryptoID)) {
          safeToSaveRef.current = true;
        }
      }, 100);

      // apply the Event listeners form AG Grid Helpers
      const cleanupEventListeners = manageGridEventListeners(
        gridRef,
        debouncedSaveColumnState,
      );
      return cleanupEventListeners; // Cleanup on unmount or dependency change
    },
    [cryptoID, debouncedSaveColumnState],
  );

  // LQC-1301 — the settings cog goes solid orange while this trade's Trade
  // Settings panel is open (keyed by panelSelectedEsidByCryptoId). The cog toggle
  // and the "Clear" button clear that selection, but closing the panel via AG
  // Grid's own chrome (X) or switching to another tool panel does not — so the
  // cog stayed orange after the panel shut. Clear the selection whenever the
  // tradeSettings panel is no longer the opened tool panel.
  //
  // LQC-1811 — this must run for mini tiles / pop-outs too (it previously
  // early-returned for them, which is why their cog stayed orange when the panel
  // was closed or another panel was shown). A tile's panel always follows the
  // tile's own trade (miniTilePanelEsidByCryptoId), so when the panel is open in
  // a tile we mirror that trade onto the cog source — this makes the cog go
  // orange even when the panel was opened from the rail tab rather than the
  // per-row cog. On the main blotter the selection is owned by the cog / dropdown,
  // so we leave it untouched while the panel is open.
  const onToolPanelVisibleChanged = useCallback(
    (params: any) => {
      if (!cryptoID) return;
      const settingsPanelOpen =
        params.api?.getOpenedToolPanel?.() === TRADE_SETTINGS_TOOL_PANEL_ID;
      if (!settingsPanelOpen) {
        tradeSettingsStore.getState().setPanelSelectedEsid(cryptoID, null);
        return;
      }
      if (isMiniTable) {
        const tileEsid =
          tradeSettingsStore.getState().miniTilePanelEsidByCryptoId[cryptoID] ??
          null;
        tradeSettingsStore.getState().setPanelSelectedEsid(cryptoID, tileEsid);
      }
    },
    [isMiniTable, cryptoID],
  );

  const onFirstDataRendered = useCallback(() => {
    // Wait for AG Grid's own confirmation before flipping the build-animation flag.
    setTimeout(() => {
      firstDataApplied.current = true;
    }, 500);
  }, []);

  // Restoring a set filter (e.g. Owner) before AG Grid has the rows makes it drop values not yet
  // present — a race that lost filters on some refreshes. So gate on a non-zero row count (not
  // just tradeDataHasReturned, which only means the API resolved) and drive it from onRowDataUpdated.
  const restoreSavedFilters = useCallback(() => {
    if (isMiniTable || !cryptoID || !gridRef.current?.api) return;
    if (!tradeDataHasReturned) return;
    if (filtersAppliedRef.current) return;
    if (gridRef.current.api.getDisplayedRowCount() <= 0) return; // rows must exist first

    const latestOpenWidgetObject = returnOpenWidgetsObject(cryptoID);
    if (latestOpenWidgetObject?.filterModel) {
      applyFiltersToAGGridTable(gridRef, latestOpenWidgetObject);
    }
    filtersAppliedRef.current = true; // done even if no saved filter, so we stop retrying
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMiniTable, cryptoID, tradeDataHasReturned]);

  // AG Grid fires this once rows are in the row model — the reliable point to restore filters.
  const onRowDataUpdated = useCallback(() => {
    restoreSavedFilters();
  }, [restoreSavedFilters]);

  // If tradeDataHasReturned flips true after the last onRowDataUpdated, restore never runs
  // and filtersAppliedRef stays stuck false — re-run it so the flag reliably settles.
  useEffect(() => {
    restoreSavedFilters();
  }, [tradeDataHasReturned, restoreSavedFilters]);

  const sideBar = useMemo(() => {
    return actionSummarySidebar({
      historicTradeDataForMainTable,
      setHistoricTradeDataForMainTable,
      // Stable for the widget's lifetime — captured once is fine. Lets the
      // Trade Settings panel bind to this tile's trade (LQC-1301).
      isMiniTable,
      cryptoID,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LQC-1301 — in a trade tile, keep the Trade Settings panel bound to the
  // tile's current trade. Mirrors the dropdown selection on the main blotter,
  // but driven by the tile rather than a user picking a Trade ID. Scoped by
  // cryptoID so multiple open tiles don't fight over one value.
  useEffect(() => {
    if (!isMiniTable || !cryptoID) return;
    const tileEsid = dataForMainTable?.esid ?? null;
    tradeSettingsStore.getState().setMiniTilePanelEsid(cryptoID, tileEsid);
    // LQC-1811 — when the user picks a different trade from the tile's dropdown
    // while the Trade Settings panel is open, the cog must follow the new
    // selection. The cog reads panelSelectedEsidByCryptoId, so mirror the tile's
    // current trade into it while the panel is open (closed → leave it grey).
    const settingsPanelOpen =
      gridRef.current?.api?.getOpenedToolPanel?.() ===
      TRADE_SETTINGS_TOOL_PANEL_ID;
    if (settingsPanelOpen) {
      tradeSettingsStore.getState().setPanelSelectedEsid(cryptoID, tileEsid);
    }
    return () => {
      tradeSettingsStore.getState().setMiniTilePanelEsid(cryptoID, null);
      // Clear the cog selection on unmount so the tile doesn't come back orange.
      tradeSettingsStore.getState().setPanelSelectedEsid(cryptoID, null);
    };
  }, [isMiniTable, cryptoID, dataForMainTable?.esid]);

  // LQC-1718 — switching workspaces unmounts this grid; AG Grid is destroyed
  // without firing toolPanelVisibleChanged, so onToolPanelVisibleChanged never
  // clears the selection and the cog stayed orange (with the panel closed) on
  // return. Clear the main-blotter selection on unmount so we come back with the
  // cog grey and the sidebar shut. cryptoID is stable after mount, so this
  // cleanup effectively only runs on unmount. Mini tiles clear their own
  // miniTilePanelEsidByCryptoId entry above.
  useEffect(() => {
    if (isMiniTable || !cryptoID) return;
    return () => {
      tradeSettingsStore.getState().setPanelSelectedEsid(cryptoID, null);
    };
  }, [isMiniTable, cryptoID]);

  const style = useMemo(() => {
    const baseStyles = GenerateActionSummaryCustomStyles(
      theme,
      tableStripeOddTrue,
    ) as React.CSSProperties;
    return {
      ...baseStyles,
      fontSize: `${getFontSizePx(fontSize)}px`,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    theme,
    tableStripeOddTrue,
    fontSize,
    tradeBlotterColorLight,
    tradeBlotterColorDark,
    tradeBlotterBidAskEnabled,
    tradeBlotterBidColorLight,
    tradeBlotterBidColorDark,
    tradeBlotterAskColorLight,
    tradeBlotterAskColorDark,
    tradeBlotterFlashColorLight,
    tradeBlotterFlashColorDark,
  ]);

  const dynamicRowHeight = useMemo(() => getRowHeight(rowHeight), [rowHeight]);

  const dynamicHeaderHeight = useMemo(
    () => getHeaderHeight(rowHeight),
    [rowHeight],
  );

  const columnWidthMultiplier = useMemo(
    () => getColumnWidthMultiplier(fontSize),
    [fontSize],
  );

  // isMiniTable = false;
  // let isMiniTableForCols: boolean | undefined = false;
  // LQC-2259 - do NOT add `theme` (or anything else cosmetic) here. A new colDefs
  // array makes AG Grid re-apply every colDef, and each explicit `hide`/`width`
  // overwrites the user's saved column state. ES-level cells take theme from CSS
  // or their own useTheme, so they don't need a rebuild.
  const columnDefs = useMemo<ColDef[]>(() => {
    return returnColDefsForMainActionSummaryTable({
      getAccessTokenSilently,
      cryptoID,
      // isMiniTableForCols, // Will hide unwanted cols if loaded from the mini table.
      gridRef,
    });
  }, [cryptoID, getAccessTokenSilently]);

  // Fresh colDefs carry fresh (unscaled) minWidths, so drop the stored bases.
  useEffect(() => {
    baseMinWidthsRef.current = {};
  }, [columnDefs]);

  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector(".GridContainer");
  }, []);

  // Manage cell selection behavior across all grids
  const { onCellMouseDown } = useCellSelectionManager();

  const detailCellRendererParams = useMemo(() => {
    return (params: ICellRendererParams) => {
      const res = {} as IDetailCellRendererParams | any; // COLDEFS not liking the 'locked position'.
      const expandedRowsContextCurrent = currentExpandedRowsContextRef.current;
      const fullTradeData = params.data;

      const colDefOptionsObj: IColDefsObject = {
        theme,
        expandedRowsContextCurrent,
        setExpandedRowsContext,
        fullTradeData,
        getAccessTokenSilently,
        setExpandedRowsContextMostRecentlyUpdated,
        cryptoID,
        columnWidthMultiplier,
        isMiniTable,
      };

      let isNotSentAndClosed = false;
      if (params.data) {
        if (params.data.type === TradeType.NotSent) {
          if (
            params.data.status == ExecutionStrategyStatus.NotDealt ||
            params.data.status == ExecutionStrategyStatus.Cancelled
          ) {
            isNotSentAndClosed = true;
          }
        }
      }

      // Create an object of all flags needed for trade flow - easier to read and pass to helper functions in object.
      const tradeDetails: ITradeDetails = {
        // booleans for main trade conditions:
        isOp: params.data.role === "OP",
        isOutright: params.data.strategyType === StrategyType.Outright,
        isRFQ: params.data.type === TradeType.RFQ,
        isNotSentAndClosed: isNotSentAndClosed,
        // booleans for alternate tables in drop down:
        isViewingAllocaitons:
          expandedRowsContextCurrent.find(
            (setting) => setting.esid === params.data.esid,
          )?.allocationsShowing ?? false,
        isEditingPanel:
          expandedRowsContextCurrent.find(
            (setting) => setting.esid === params.data.esid,
          )?.editPanelShowing ?? false,
        // booleans for status
        isViewingLegs:
          expandedRowsContextCurrent.find(
            (setting) => setting.esid === params.data.esid,
          )?.legsShowing ?? false,
        isTradeStatusNew: params.data.status == ExecutionStrategyStatus.New,
        isUnwind: params.data.unwind,
        isCancelledOPWithNoCP:
          params.data.status == ExecutionStrategyStatus.Cancelled &&
          params.data.role == "OP" &&
          params.data.executionRequestCounterParties?.length == 0,
      };

      res.getDetailRowData = (params: GetDetailRowDataParams<any>) => {
        return returnRowDataAtDetailTable(
          params,
          tradeDetails,
          getAccessTokenSilently,
        );
      };

      // NESTED TABLE 1 ER for the OP - This will use ES data for the RP.
      res.detailGridOptions = {
        // Selecting cells
        onCellMouseDown: onCellMouseDown,
        // Selecting cells
        enableRangeSelection: true,
        suppressMultiRangeSelection: false,
        suppressRowClickSelection: false,
        //
        popupParent: document.querySelector(".GridContainer"),
        tooltipShowDelay: 0,
        columnMenu: "new", //do not delete, affects header menus
        rowHeight: dynamicRowHeight,
        headerHeight: dynamicHeaderHeight,
        detailRowAutoHeight: true,
        singleClickEdit: true,
        className: "cssStylingOnNestedTable1",
        getRowId: (nestedParams: GetRowIdParams<any>) => {
          const id = returnRowIDAtFirstDetailTable(
            params,
            nestedParams,
            tradeDetails,
          );
          return id;
        }, // All of the right click options.
        getContextMenuItems: (params: GetContextMenuItemsParams) => {
          return GetExecutionContextMenuItems(params, {
            fullTradeData,
            getAccessTokenSilently,
            expandedRowsContextCurrent,
            setExpandedRowsContext,
            setExpandedRowsContextMostRecentlyUpdated,
            isERLevel: true,
            cryptoID: cryptoID,
          });
        },
        columnDefs: returnColDefsForDetailTableAtActionSummary(
          colDefOptionsObj,
          tradeDetails,
        ),
        defaultColDef: {
          ...defaultColDef,
          suppressKeyboardEvent: suppressMultiCellDelete,
        },
        masterDetail: masterDetialAtERLevel(tradeDetails),
        groupDefaultExpanded: setLegTableToExpanded(tradeDetails),
        stopEditingWhenCellsLoseFocus: true,
        processDataFromClipboard: processDataFromClipboardPreventMulti,
        onRowGroupOpened: (params: any) =>
          handleShowOrHideColsFromNodeExpand(params, gridRef, fullTradeData),

        // NESTED TABLE 2
        detailCellRendererParams: {
          getDetailRowData: (nestedParams: GetDetailRowDataParams<any>) => {
            return returnRowDataAtNestedLegTable(
              nestedParams,
              tradeDetails,
              fullTradeData,
            );
          },
          detailGridOptions: {
            onCellMouseDown: onCellMouseDown,
            enableRangeSelection: true,
            suppressMultiRangeSelection: false,
            suppressRowClickSelection: true,
            //
            tooltipShowDelay: 0,
            columnMenu: "new", //do not delete, affects header menus
            className: "cssStylingOnNestedTable2",
            rowHeight: dynamicRowHeight,
            headerHeight: dynamicHeaderHeight,
            detailRowAutoHeight: true,
            singleClickEdit: true,
            getRowId: (nestedParams: GetRowIdParams) => {
              // Fill rows need unique IDs distinct from block rows
              // Use ExtFillId for stable row identification (survives backend fillId changes)
              // Include blockId to avoid collisions across legs (each leg's fills start at ExtFillId 1)
              if (nestedParams.data?.rowType === "fill") {
                const stableFillId =
                  nestedParams.data.fillData?.extFillId ??
                  nestedParams.data.fillData?.fillId;
                const blockId =
                  nestedParams.data.fillData?.blockId ??
                  nestedParams.data.blockId;
                return `fill-${blockId}-${stableFillId}`;
              }
              return String(nestedParams.data?.blockId);
            },
            columnDefs: tradeDetails.isOp
              ? returnLegColDefsOP(colDefOptionsObj)
              : returnLegColDefsRP(colDefOptionsObj),
            defaultColDef: {
              ...defaultColDef,
              lockPosition: true,
              suppressKeyboardEvent: suppressMultiCellDelete,
            },
            stopEditingWhenCellsLoseFocus: true,
            processDataFromClipboard: processDataFromClipboardPreventMulti,
            getContextMenuItems: (params: GetContextMenuItemsParams) => {
              return GetExecutionContextMenuItems(params, {
                fullTradeData,
                getAccessTokenSilently,
                expandedRowsContextCurrent,
                setExpandedRowsContext,
                setExpandedRowsContextMostRecentlyUpdated,
                isERLevel: false,
                cryptoID: cryptoID,
              });
            },
            groupDefaultExpanded: 1,
            popupParent: document.querySelector(".GridContainer"),
            // External filter for Order fills - controls expand/collapse of fill rows
            // without adding an extra auto-group column (which breaks headers & alignment).
            // The collapsedFillBlocks Set is shared via context so cell renderers and
            // the header component can toggle it, then call api.onFilterChanged().
            ...(() => {
              const collapsedFillBlocks = new Set<number>();
              return {
                context: { collapsedFillBlocks },
                isExternalFilterPresent: () => {
                  return masterDetailAtLegLevelForOrders(
                    fullTradeData.type,
                    fullTradeData.esid,
                    tradeDetails.isEditingPanel,
                  );
                },
                doesExternalFilterPass: (node: any) => {
                  if (node.data?.rowType !== "fill") return true;
                  return !collapsedFillBlocks.has(node.data.parentBlockId);
                },
              };
            })(),
          },
        },
      };
      return res;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, dynamicRowHeight, dynamicHeaderHeight, columnWidthMultiplier]);

  //Capture every redraw/refresh cell needed to AS table
  useEffect(() => {
    allASChangedData.forEach((trade: IFindChangedDataOutput) => {
      handleRedrawingRowsForActionSummaryTable({
        gridRef,
        trade,
        expandedRowsContext,
        setExpandedRowsContext,
        setExpandedRowsContextMostRecentlyUpdated,
        getAccessTokenSilently,
      });

      //redraw between Quoted Awarded to regenerate quotedSize fields for SYN REPO & SYN REV REPO
      if (
        trade.role === "RP" &&
        trade.changes?.status &&
        ((trade.changes.status.oldValue === ExecutionStrategyStatus.Quoted &&
          trade.changes.status.newValue === ExecutionStrategyStatus.Awarded) ||
          (trade.changes.status.oldValue === ExecutionStrategyStatus.Awarded &&
            trade.changes.status.newValue === ExecutionStrategyStatus.Quoted))
      ) {
        redrawRowsViaESID(trade.id, gridRef);
      }
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allASChangedData]);

  useEffect(() => {
    currentExpandedRowsContextRef.current = expandedRowsContext;
    redrawRowsViaESID(expandedRowsContextMostRecentlyUpdated, gridRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRowsContext]);

  // Effect to scale column widths when font size changes
  useEffect(() => {
    if (gridRef.current?.api) {
      const allColumns = gridRef.current.api.getColumns();
      if (allColumns) {
        allColumns.forEach((column) => {
          const colDef = column.getColDef() as any;
          if (colDef.fixedWidth) return; // Skip fixed-width control columns
          if (colDef.width) {
            const baseWidth = colDef.width;
            const newWidth = Math.round(baseWidth * columnWidthMultiplier);
            gridRef.current?.api.setColumnWidth(column, newWidth);
          }
          if (colDef.minWidth) {
            // Scale from the ORIGINAL minWidth, not the live (already-scaled)
            // colDef value. We mutate colDef.minWidth below so AG Grid honours
            // the new resize floor; without a stored base this would compound
            // downward on every font-size change.
            const colId = column.getColId();
            if (baseMinWidthsRef.current[colId] == null) {
              baseMinWidthsRef.current[colId] = colDef.minWidth;
            }
            const baseMinWidth = baseMinWidthsRef.current[colId];
            const newMinWidth = Math.round(
              baseMinWidth * columnWidthMultiplier,
            );
            column.getColDef().minWidth = newMinWidth;
          }
        });
      }
    }
  }, [fontSize, columnWidthMultiplier]);

  function handleActionSummaryFilterChange(event: any) {
    actionSummaryFilterChangeHandler(event, previousFilterModel);
    // Mirror the header's orange active-filter state onto the sidebar tab.
    setIsAnyFilterActive(event.api.isAnyFilterPresent());

    // A user filter change (not a data-driven re-eval) on a loaded grid means the live
    // filter is authoritative — mark restore done so the save doesn't fall back to the
    // stale stored filterModel (which was reverting removals, e.g. clearing Status).
    if (!isMiniTable && tradeDataHasReturned && !event.afterDataChange) {
      filtersAppliedRef.current = true;
    }
  }

  // Helper function to adjust color brightness (for stripe variant)
  const adjustBrightness = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  // Get row style based on bid/ask
  const getRowStyle = useCallback(
    (params: any) => {
      if (!tradeBlotterBidAskEnabled) {
        return undefined; // Use default styling from CSS
      }

      const data = params.data;
      if (!data) return undefined;

      // Determine quote side based on role
      let quoteSide: string | null = null;
      if (data.role === "OP" && data.quoteSideES) {
        quoteSide = data.quoteSideES;
      } else if (data.role === "RP" && data.quoteSideER) {
        quoteSide = data.quoteSideER;
      }

      // If no quote side, return undefined to use default row color
      if (!quoteSide) {
        return undefined;
      }

      // Determine base color based on bid/ask and theme
      let baseColor: string;
      if (quoteSide.toLowerCase() === "bid") {
        baseColor =
          theme === "light"
            ? tradeBlotterBidColorLight
            : tradeBlotterBidColorDark;
      } else if (quoteSide.toLowerCase() === "ask") {
        baseColor =
          theme === "light"
            ? tradeBlotterAskColorLight
            : tradeBlotterAskColorDark;
      } else {
        return undefined;
      }

      // Apply stripe if needed
      if (tableStripeOddTrue && params.node.rowIndex % 2 !== 0) {
        const adjustAmount = theme === "light" ? -5 : 5;
        baseColor = adjustBrightness(baseColor, adjustAmount);
      }

      // Create hover color as a darker/lighter shade
      const hoverAdjustAmount = theme === "light" ? -10 : 10;
      const hoverColor = adjustBrightness(baseColor, hoverAdjustAmount);

      return {
        background: baseColor,
        "--ag-row-hover-color": hoverColor,
      };
    },
    [
      tradeBlotterBidAskEnabled,
      theme,
      tableStripeOddTrue,
      tradeBlotterBidColorLight,
      tradeBlotterBidColorDark,
      tradeBlotterAskColorLight,
      tradeBlotterAskColorDark,
    ],
  );

  return (
    <RequestPendingWrapper rowData={rowData ?? []}>
      <div
        className={`${theme == "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"} ${isMiniTable ? "" : "mt-3"} ${isAnyFilterActive ? "filterActiveSideBar" : ""} ${historicTradeDataForMainTable.viewingHistoricalTable ? "historicalSearchActiveSideBar" : ""} topAlignedDetailRows h-full w-full border-l-2 border-t border-[#E7EAEB] pr-1 dark:border-t-2 dark:border-[#252525]`}
        style={style} // controls the colour of the odd and even rows in the tables.
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          overlayLoadingTemplate={AG_GRID_LOADING_TEMPLATE}
          // LQC-2482: AG Grid 31 shows nothing when a filter leaves 0 rows. Guard on
          // undefined rowData so the Loading overlay survives the initial fetch.
          // Re-evaluate on upgrade to >= 35 (built-in "No matching rows" overlay).
          onModelUpdated={(params) =>
            syncNoRowsOverlay(params.api, rowData === undefined)
          }
          noRowsOverlayComponent={AGGridNoRowsOverlay}
          columnDefs={columnDefs}
          onFilterChanged={handleActionSummaryFilterChange}
          columnMenu={"new"} //do not delete, affects header menus
          defaultColDef={defaultColDefActionSummary}
          autoGroupColumnDef={autoGroupColumnDef}
          masterDetail={true}
          detailCellRendererParams={detailCellRendererParams}
          detailRowAutoHeight={true}
          onGridReady={onGridReady}
          onToolPanelVisibleChanged={onToolPanelVisibleChanged}
          onFirstDataRendered={onFirstDataRendered}
          onRowDataUpdated={onRowDataUpdated}
          rowGroupPanelShow={!isMiniTable ? "always" : "never"}
          // Method of selecting rows
          suppressRowClickSelection={true}
          stopEditingWhenCellsLoseFocus={true}
          rowSelection={"multiple"}
          suppressAggFuncInHeader={true}
          suppressDragLeaveHidesColumns={true}
          // Style:
          rowHeight={dynamicRowHeight}
          headerHeight={dynamicHeaderHeight}
          singleClickEdit={true}
          scrollbarWidth={6}
          getRowStyle={getRowStyle}
          // adding params to the grid for the row grouping. Having the fractions in the status heading has taken away the status name when the field is added to the row grouping.
          groupDisplayType={"groupRows"}
          // Miscellaneous:
          getRowId={getRowId} // Allows the grid to keep track of where the row was for better performance and to not lose context on changes. EG: If a row is selected when a new row is created, the selected row will remain selected.
          animateRows={hasBuiltGrid} // Smoother transition of rows when updates occur - needs 'getRowId' for this to work.
          maintainColumnOrder={true}
          sideBar={sideBar}
          popupParent={popupParent}
          // LQC-2678 - records where the right-click landed so the menu's Edit
          // Expiry item can anchor the popover there. AG Grid hands the item's
          // action() no event of its own.
          onCellContextMenu={(event) =>
            recordExpiryAnchorFromEvent(event.event)
          }
          // Double clicking on a table for custom actions.
          getContextMenuItems={(params) =>
            GetContextMenuItemsActionSummary(
              params,
              user,
              getAccessTokenSilently,
              expandedRowsContext,
              setExpandedRowsContext,
              setExpandedRowsContextMostRecentlyUpdated,
              expandedRowsContextCurrent,
              cryptoID,
              theme,
            )
          }
          components={{
            customConfigsToolPanel: HistoricalSearchSidebarContent,
            ...(featureFlagStore.getState().tradeSettingsSidebarAndCog
              ? { tradeSettingsToolPanel: TradeSettingsSidebarContent }
              : {}),
            SelectPanelIndex: SelectPanelIndex,
            DynamicCellEditorQuoteDollarFraction:
              DynamicCellEditorQuoteDollarFraction,
          }}
          icons={getActionSummarySidebarIcons()}
          groupDefaultExpanded={
            !isMiniTable ? 0 : rowData?.length == 1 ? -1 : 0
          } // -1 expands the table so the mini tile always shows as expanded. If the mini table is open and a Historic trade dataset is loaded, this will prevent them from getting expanded.
          tooltipShowDelay={1}
          domLayout="normal"
          // WARNING - Setting the reactiveCustomComponents BELOW WILL BREAK the quote editing on the main page / Main Action Summary table / ES (top) LEVEL ability to quote. Cannot set reactiveCustomComponents={true} until logic is moved to custom cell renderers and NOT using value getters.
          // WARNING ----   reactiveCustomComponents={false}  /  reactiveCustomComponents={true} -----------
          // WARNING - Setting the reactiveCustomComponents ABOVE WILL BREAK the quote editing on the main page.
        />
        {/* Right-click "Edit Expiry" has no React tree of its own, so the
            popover is hosted here and targeted through expiryEditStore. */}
        <ExpiryEditPopoverHost cryptoID={cryptoID} />
      </div>
    </RequestPendingWrapper>
  );
};

export default ActionSummaryTable;
